import WRAP from "WRAP-JS";
import { configAscScaleLayer } from "./config/ConfigAscScale";
import {
  _orderBy, calcPressureFromAlt,
  formatIssueTime,
  formatValidTime,
  ft2m,
  parseUTCDate
} from "../../../utils/FPEUtils";
import LegendAscScale from "../legend/LegendAscScale";
import { WX_ID_ASCSCALE } from "../../../store/WeatherContentsState";

class AscScale {
  static NAME = WX_ID_ASCSCALE;
  static TURBULENCE_NAME = `${AscScale.NAME}_TURBULENCE`;
  static CONVECTION_NAME = `${AscScale.NAME}_CONVECTION`;
  static ICING_NAME = `${AscScale.NAME}_ICING`;

  DATA_LEVEL = [100, 150, 200, 250, 300, 400, 450, 500, 700, 850];

  contentsVisibleState = {
    [AscScale.TURBULENCE_NAME]: true,
    [AscScale.CONVECTION_NAME]: true,
    [AscScale.ICING_NAME]: true
  };

  dh;
  layer;
  ready = false;
  etd;
  elapsed = 0;
  baseTime = '---';
  issueTime = '---';
  validTime = '---';
  altitude = 0;
  level;
  dispatcher;
  updateInterval;
  visible;

  constructor() {
    this.layer = new WRAP.Geo.Layer('AscScale');
    this.layer.setVisible(false);
    this.layer.disableInteraction(true);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = WRAP.DH.addObject('ASCScale');
      this.dh.inspect(ref => {
        this._selectBaseValidTime(ref);
        this.updateInterval = ref._data.conf.Attributes.UpdateInterval;
        resolve();
      });
    });
  }

  initLayer() {
    this.layer.configure(this.dh, configAscScaleLayer);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this.layer;
  }

  setVisible(visible) {
    if (!this.ready) return;
    
    if (this.layer.visible() === visible) {
      return;
    }
    if (visible) {
      this.layer.show();
    } else {
      this.layer.hide();
    }
    if (visible) {
      this.dh.setUpdateInterval(this.updateInterval);
    } else {
      this.dh.setUpdateInterval(0);
    }
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  setContentsVisible(name, visible) {
    this.contentsVisibleState[name] = visible;
    this._updateDataset();
  }

  getTimeInformation() {
    const issueTime = this.issueTime !== '---' ? formatIssueTime(this.issueTime) : this.issueTime;
    const validTime = this.validTime !== '---' ? formatValidTime(this.validTime) : this.validTime;
    return {
      id: AscScale.NAME,
      label: 'ASCScale',
      baseTime: issueTime,
      validTime: validTime
    };
  }

  getLegend() {
    return {
      id: AscScale.NAME,
      label: 'ASCScale',
      component: LegendAscScale,
      contents: this
    }
  }

  getContentsVisibleState() {
    return this.contentsVisibleState;
  }

  getIndividualAltitude() {
    return {
      id: AscScale.NAME,
      altitude: this.altitude,
      label: 'ASCScale',
      tooltip: this._altitudeTooltip
    };
  }

  getAltitudeLabel() {
    return this._altitudeTooltip(this.altitude);
  }

  applyIndividualAltitude(altitude) {
    if (this.altitude !== altitude) {
      this.altitude = altitude;
      const level = this._getAscScaleLevelByAltitude(this.altitude);
      if (this.level !== level) {
        this._updateDataset();
      }
    }
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if (!this.ready) return;

    this._selectBaseValidTime(this.dh);
  }

  applyPlanETD(etd) {
    this.etd = etd;

    if (!this.ready) return;

    this._selectBaseValidTime(this.dh);
  }

  _selectBaseValidTime(ref) {
    let timeList = ref.query('timelist').value();
    if (!timeList) return;

    let targetTime;
    if (!this.etd) {
      //プラン未選択時
      targetTime = Date.now();
    } else {
      //プラン選択時
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    //最新のissuetimeを取得
    const latestIssueTime = _orderBy(timeList, ['issuetime'], ['desc'])[0];
    const tmpTimeList = latestIssueTime.validtime.map(validtime => {
      return {
        "validtime": validtime,
        "basetime": latestIssueTime.basetime,
        "issuetime": latestIssueTime.issuetime,
        "validtime_msec": parseUTCDate(validtime).getTime()
      };
    });

    // targetTimeに最も近いvalidを選択する(直近が過去時制と未来時制で同時刻の場合、未来時制のValidデータを採用する。)
    const sortedTimelist = _orderBy(tmpTimeList, [
      o => {
        return Math.abs(targetTime - o.validtime_msec);
      },
      'validtime'
    ], ['asc', 'desc']);

    const selectedTime = sortedTimelist[0];

    if (selectedTime && this.validTime !== selectedTime.validtime) {
      this.baseTime = selectedTime.basetime;
      this.issueTime = selectedTime.issuetime;
      this.validTime = selectedTime.validtime;
      this._updateDataset();
    }
  }

  _updateDataset() {
    if (this.issueTime !== '---' && this.validTime !== '---') {
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      const element = this._getElementByContentsState(this.contentsVisibleState);
      this.level = this._getAscScaleLevelByAltitude(this.altitude);
      this.layer.set({
        element: element,
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime,
        use: 'map'
      });
    }
  }

  _getElementByContentsState(state) {
    const element = [];
    if (state[AscScale.TURBULENCE_NAME]) {
      element.push('TURB');
    }
    if (state[AscScale.CONVECTION_NAME]) {
      element.push('CONV');
    }
    if (state[AscScale.ICING_NAME]) {
      element.push('ICING');
    }
    return element;
  }

  _getAscScaleLevelByAltitude(altitude) {
    return this._fl2mb(altitude) + '';
  }

  _fl2mb(fl) {
    //flightLevel → meter → mb
    const m = ft2m(fl * 100);
    const mb = calcPressureFromAlt(m);
    const near = _orderBy(this.DATA_LEVEL,[
      o => {
        return Math.abs(mb - o);
      },
      o => {
        return o;
      }
    ])
    return near[0];
  }

  _altitudeTooltip(altitude) {
    const ascScale = [
      {fl: 530, mb: 100},
      {fl: 450, mb: 150},
      {fl: 390, mb: 200},
      {fl: 340, mb: 250},
      {fl: 300, mb: 300},
      {fl: 240, mb: 400},
      {fl: 210, mb: 450},
      {fl: 180, mb: 500},
      {fl: 100, mb: 700},
      {fl: 50, mb: 850}
    ];
    const m = ft2m(altitude * 100);
    const mb = calcPressureFromAlt(m);
    const near = _orderBy(ascScale, [
      o => {
        return Math.abs(mb - o.mb);
      },
      o => {
        return o;
      }
    ])[0];
    const fl = ('000' + near.fl).slice(-3);
    return `FL${fl} / ${near.mb}hPa`;
  }
}

export default AscScale;
